import { BaseWithId } from '@app/shared/models/base';
import { Client } from '@app/shared/models/client';
import { AuthUser } from '@app/shared/models/auth-user';
import { LogicOp, Permission, Role, Page, Module } from '@app/core/authentication/permission';
import { UserRole } from '@app/shared/models/user-role';
import { SessionType } from './session-type';
import { UserCountry } from '@app/shared/models/user-country';

export class User extends BaseWithId {
    public username: string;
    public password: string;
    public accessToken: string;

    public user: AuthUser;
    public client: Client;
    public country: UserCountry;

    public isMultiClientUser: boolean;

    public sessionType: SessionType;

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            user: new AuthUser(json && json.user),
            client: new Client(json && json.client),
            country: new UserCountry(json && json.country)
        });
    }


    /*
    * METHOD (same goes for modules, pages, roles, ...
    * - takes a Permission or array Permission[]
    * - optional param 'logic' (AND, OR, NAND, NOR) which defaults to AND if nothing is provided
    *   -> AND: The user must have ALL of the Permissions in order to return TRUE
    *   -> OR: The user must have ONE of the Permissions in order to return TRUE
    *   -> NAND: The user must NOT have ALL the Permissions in order to return TRUE
    *   -> NOR: The user must NOT have ANY of the Permissions in order to return TRUE
    */

    public hasPermissions(permissions: Permission | Permission[], logic = LogicOp.AND): boolean {
        return this.user && this.user.permissions && this.checkStringsInArray(permissions, this.user.permissions, logic);
    }

    public hasModules(modules: Module | Module[], logic = LogicOp.AND): boolean {
        return this.user && this.user.modules && this.checkStringsInArray(modules, this.user.modules, logic);
    }

    public isAllowedToOpenPage(pages: Page | Page[], logic = LogicOp.AND): boolean {
        return this.user && this.user.pages && this.checkStringsInArray(pages, this.user.pages, logic);
    }

    public hasRoles(roles: Role | Role[], logic = LogicOp.AND): boolean {
        let roleInStringFormat;

        if (this.user.roles && this.user.roles.length > 0) {
            roleInStringFormat = typeof this.user.roles[0] === 'string';
        }

        const userRoles: Role[] = roleInStringFormat ?
                                  this.user.roles as Role[] :
                                  (<UserRole[]>this.user.roles).map((roleObj: UserRole) => roleObj.role_id);

        return this.user && this.user.roles && this.checkStringsInArray(roles, userRoles, logic);
    }

    protected checkStringsInArray(strings: string | string[], list: string[], logic = LogicOp.AND): boolean {
        strings = Array.isArray(strings) ? strings : [strings];
        list = list || [];

        switch (logic) {
            case LogicOp.AND: {
                return strings.every(reqRole => !!list.find(role => role === reqRole));
            }
            case LogicOp.OR: {
                return strings.some(reqRole => !!list.find(role => role === reqRole));
            }
            case LogicOp.NAND: {
                return !strings.every(reqRole => !!list.find(role => role === reqRole));
            }
            case LogicOp.NOR: {
                return !strings.some(reqRole => !!list.find(role => role === reqRole));
            }
            default: {
                return false;
            }
        }
    }

}
